import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import type { Id } from "@teachloop/backend/_generated/dataModel";
import { cn } from "@teachloop/ui/utils";
import { useLocalSearchParams, useRouter } from "expo-router";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Pressable, ScrollView, Text, View } from "react-native";
import ShareIcon from "@/assets/svgs/share-icon";
import { TutorReviewSummaryOrEmpty } from "@/elements/components/find-tutor/tutor-review-summary";
import TutorProfileReviewsList from "@/elements/components/find-tutor/tutor-profile-reviews-list";
import TutorReviewModal from "@/elements/components/find-tutor/tutor-review-modal";
import ExamBoardMarkerIcon from "@/assets/svgs/exam-board-marker-icon";
import ExperienceIcon from "@/assets/svgs/experience-icon";
import RemoteIcon from "@/assets/svgs/remote-icon";
import SubjectIcon from "@/assets/svgs/subject-icon";
import UserIcon from "@/assets/svgs/user-icon";
import Loader from "@/elements/components/shared/loader";
import ResponsiveContent from "@/elements/components/shared/responsive-content";
import { useSheet } from "@/elements/components/shared/sheet-provider";
import type {
  ChildrenSelectSheetPayload,
  ChildrenSelectSheetResult,
} from "@/elements/components/sheets/children-select-sheet";
import { AppBar } from "@/elements/components/ui/app-bar";
import { Avatar } from "@/elements/components/ui/avatar";
import { Button } from "@/elements/components/ui/button";
import { Tab } from "@/elements/components/ui/tab";
import { Tag } from "@/elements/components/ui/tag";
import { api } from "@/elements/components/utils/convex-api";
import goBack from "@/elements/components/utils/go-back";
import { shareTutorProfile } from "@/elements/components/utils/share-tutor-profile";
import { SheetIds } from "@/elements/components/utils/sheet-ids";
import { isIbCurriculumLabel } from "@/elements/components/utils/curriculum";

function getChildIdFromParams(
  childId: string | string[] | undefined,
): string | undefined {
  if (typeof childId === "string") return childId;
  if (Array.isArray(childId)) return childId[0];
  return undefined;
}

type CurriculumOption = {
  id: string;
  label: string;
  isIb: boolean;
};

type SubjectWithYearGroups = {
  subjectId: string;
  subjectLabel: string;
  yearGroups: {
    yearGroupId: string;
    yearGroupLabel: string;
    isIb: boolean;
  }[];
};

function TutorProfileScreen() {
  const router = useRouter();
  const scrollViewRef = useRef<ScrollView>(null);
  const scrollContentRef = useRef<View>(null);
  const reviewsSectionRef = useRef<View>(null);
  const childResolutionStartedRef = useRef(false);
  const [isReviewModalVisible, setIsReviewModalVisible] = useState(false);
  const [selectedCurriculumId, setSelectedCurriculumId] = useState<
    string | null
  >(null);
  const { tutorId, childId: childIdParam } = useLocalSearchParams<{
    tutorId: string;
    childId?: string | string[];
  }>();
  const childIdFromUrl = getChildIdFromParams(childIdParam);
  const [resolvedChildId, setResolvedChildId] = useState<string | undefined>();
  const [isChildResolved, setIsChildResolved] = useState(false);

  const { data: currentUser, isSuccess: isCurrentUserSuccess } = useQuery(
    convexQuery(api.account.queries.getCurrentUser, {}),
  );
  const persona = currentUser?.persona;
  const isParent = persona === "parent";

  const { open: openChildrenSelect } = useSheet<
    ChildrenSelectSheetPayload,
    ChildrenSelectSheetResult
  >(SheetIds.CHILDREN_SELECT);

  const { data: children = [], isSuccess: isChildrenSuccess } = useQuery({
    ...convexQuery(api.parent.findTutors.queries.children, {}),
    enabled: isParent && isCurrentUserSuccess,
  });

  const { data: tutor } = useQuery({
    ...convexQuery(api.parent.tutor.queries.getTutorProfile, {
      tutorId: tutorId as Id<"tutorProfiles">,
    }),
    enabled: !!tutorId && isParent,
  });

  const curricula = useMemo((): CurriculumOption[] => {
    if (tutor?.curricula?.length) return tutor.curricula;
    return (tutor?.curriculumLabels ?? []).map((label, index) => ({
      id: String(index),
      label,
      isIb: isIbCurriculumLabel(label),
    }));
  }, [tutor?.curricula, tutor?.curriculumLabels]);

  useEffect(() => {
    if (!curricula.length) return;
    if (
      selectedCurriculumId &&
      curricula.some((curriculum) => curriculum.id === selectedCurriculumId)
    ) {
      return;
    }
    const british = curricula.find((curriculum) => !curriculum.isIb);
    setSelectedCurriculumId((british ?? curricula[0]).id);
  }, [curricula, selectedCurriculumId]);

  const selectedCurriculum = useMemo(
    () =>
      curricula.find((curriculum) => curriculum.id === selectedCurriculumId) ??
      curricula[0],
    [curricula, selectedCurriculumId],
  );

  const subjectsForSelectedCurriculum = useMemo(() => {
    const subjects =
      (tutor?.subjectsWithYearGroups as SubjectWithYearGroups[] | undefined) ??
      [];
    if (!selectedCurriculum) return subjects;

    return subjects
      .map((subject) => ({
        ...subject,
        yearGroups: subject.yearGroups.filter((yearGroup) =>
          selectedCurriculum.isIb ? yearGroup.isIb : !yearGroup.isIb,
        ),
      }))
      .filter((subject) => subject.yearGroups.length > 0);
  }, [tutor?.subjectsWithYearGroups, selectedCurriculum]);

  const { data: tutorStudentSubjects = [], isSuccess: isTutorStudentSubjectsReady } =
    useQuery({
      ...convexQuery(
        api.parent.bookLesson.queries.getTutorStudentSubjects,
        tutorId && resolvedChildId
          ? {
              tutorId: tutorId as Id<"tutorProfiles">,
              studentId: resolvedChildId as Id<"students">,
            }
          : "skip",
      ),
    });

  const canBookWithChild =
    isTutorStudentSubjectsReady && tutorStudentSubjects.length > 0;

  const { data: canBookIntro } = useQuery(
    convexQuery(
      api.parent.tutor.queries.canCurrentUserBookIntro,
      tutorId && resolvedChildId
        ? {
            tutorId: tutorId as Id<"tutorProfiles">,
            studentId: resolvedChildId as Id<"students">,
          }
        : "skip",
    ),
  );

  const { data: approvedReviews = [] } = useQuery({
    ...convexQuery(api.parent.tutor.queries.getApprovedTutorReviews, {
      tutorId: tutorId as Id<"tutorProfiles">,
    }),
    enabled: !!tutorId,
  });

  const { data: reviewEligibility, refetch: refetchReviewEligibility } =
    useQuery({
      ...convexQuery(api.parent.tutor.queries.getTutorReviewEligibility, {
        tutorId: tutorId as Id<"tutorProfiles">,
        studentId: resolvedChildId as Id<"students">,
      }),
      enabled: !!tutorId && !!resolvedChildId,
    });

  const reviewSummary = useMemo(() => {
    if (approvedReviews.length === 0) {
      return { averageRating: null, reviewCount: 0 };
    }

    const totalRating = approvedReviews.reduce(
      (sum, review) => sum + review.rating,
      0,
    );

    return {
      averageRating: totalRating / approvedReviews.length,
      reviewCount: approvedReviews.length,
    };
  }, [approvedReviews]);

  const handleShareProfile = useCallback(async () => {
    if (!tutorId || !tutor?.name) return;

    try {
      await shareTutorProfile({
        tutorId,
        tutorName: tutor.name,
      });
    } catch {
      // User cancelled share or platform unsupported
    }
  }, [tutor?.name, tutorId]);

  const scrollToReviews = useCallback(() => {
    if (!reviewsSectionRef.current || !scrollContentRef.current) return;

    reviewsSectionRef.current.measureLayout(
      scrollContentRef.current,
      (_x, y) => {
        scrollViewRef.current?.scrollTo({ y: y - 16, animated: true });
      },
      () => {},
    );
  }, []);

  useEffect(() => {
    if (!tutorId) {
      router.back();
    }
  }, [router, tutorId]);

  useEffect(() => {
    if (!isParent || !isChildrenSuccess || !tutorId) return;

    if (!childIdFromUrl) return;

    const ownsChild = children.some((child) => child._id === childIdFromUrl);
    if (ownsChild) {
      setResolvedChildId(childIdFromUrl);
      setIsChildResolved(true);
      return;
    }

    childResolutionStartedRef.current = false;
    setResolvedChildId(undefined);
    setIsChildResolved(false);
    router.replace(`/(app)/home/find-tutor/${tutorId}`);
  }, [
    childIdFromUrl,
    children,
    isChildrenSuccess,
    isParent,
    router,
    tutorId,
  ]);

  useEffect(() => {
    if (!isCurrentUserSuccess) return;

    if (!isParent) {
      router.replace("/(app)/home");
    }
  }, [isCurrentUserSuccess, isParent, router]);

  useEffect(() => {
    if (!isParent || !isChildrenSuccess || isChildResolved) return;
    if (childResolutionStartedRef.current) return;
    childResolutionStartedRef.current = true;

    const [firstChild] = children;

    if (children.length === 0) {
      router.replace("/(app)/home/find-tutor");
      return;
    }

    if (children.length === 1 && firstChild) {
      setResolvedChildId(firstChild._id);
      setIsChildResolved(true);
      return;
    }

    (async () => {
      const result = await openChildrenSelect({
        title: "View tutor profile for",
        options: children.map((child) => ({
          label: child.name,
          value: child._id,
          imageUrl: child.imageUrl ?? null,
        })),
        preSelectedOption: firstChild?._id,
      });

      if (result?.selectedOption) {
        setResolvedChildId(String(result.selectedOption));
        setIsChildResolved(true);
        return;
      }

      router.back();
    })();
  }, [
    children,
    isChildResolved,
    isChildrenSuccess,
    isParent,
    openChildrenSelect,
    router,
  ]);

  if (
    !isCurrentUserSuccess ||
    !tutorId ||
    !isParent ||
    !isChildResolved ||
    !tutor
  ) {
    return <Loader />;
  }

  return (
    <View className="flex-1 bg-teachLoop-creamBase">
      <AppBar>
        <AppBar.Icon onPress={goBack} />
        <AppBar.Title>Back</AppBar.Title>
        <Pressable
          onPress={() => void handleShareProfile()}
          accessibilityRole="button"
          accessibilityLabel="Share tutor profile"
          className="ml-auto h-[24px] w-[24px] items-center justify-center active:opacity-70"
        >
          <ShareIcon />
        </Pressable>
      </AppBar>
      <ScrollView ref={scrollViewRef} showsVerticalScrollIndicator={false}>
        <ResponsiveContent>
          <View ref={scrollContentRef} collapsable={false}>
            <View className="h-3" />
            <View className="mx-5 gap-8">
            <View className="gap-4">
              <View className="gap-2">
                <Avatar size="md" className="bg-gray-400">
                  <Avatar.Content
                    size="md"
                    labelSize="md"
                    label={tutor?.name ?? ""}
                    source={
                      tutor?.imageUrl ? { uri: tutor.imageUrl } : undefined
                    }
                  />
                </Avatar>

                <View className="gap-2">
                  <View className="flex-row items-center justify-between gap-2">
                    <Text className="font-geologica-medium font-medium text-xl text-black leading-[1.5]">
                      {tutor?.name}
                    </Text>

                    <View className="flex-row items-center gap-2">
                      <UserIcon
                        className="size-4"
                        pathClassName={cn(
                          tutor?.teachingPreference?.includes("in_person")
                            ? "stroke-teal-600"
                            : "stroke-gray-400/50",
                        )}
                      />

                      <RemoteIcon
                        className="size-4"
                        pathClassName={cn(
                          tutor?.teachingPreference?.includes("remote")
                            ? "stroke-teal-600"
                            : "stroke-gray-400/50",
                        )}
                      />
                    </View>
                  </View>

                  <Text className="font-geologica-regular font-regular text-[14px] text-dark leading-[1.5]">
                    {[...new Set(tutor?.subjects)].join(", ")} tutor
                  </Text>

                  <TutorReviewSummaryOrEmpty
                    averageRating={reviewSummary.averageRating}
                    reviewCount={reviewSummary.reviewCount}
                    onReviewCountPress={
                      reviewSummary.reviewCount > 0
                        ? scrollToReviews
                        : undefined
                    }
                  />

                  <View className="flex-row items-center gap-2">
                    <SubjectIcon
                      className="size-5"
                      pathClassName="stroke-black"
                    />
                    <ScrollView
                      horizontal
                      contentContainerClassName="gap-2"
                      showsHorizontalScrollIndicator={false}
                    >
                      {[...new Set(tutor?.subjects)].map((subject) => (
                        <Tag size={"sm"} key={subject}>
                          <Tag.Text size={"sm"}>{subject}</Tag.Text>
                        </Tag>
                      ))}
                    </ScrollView>
                  </View>

                  <View className="flex-row items-center gap-2">
                    <ExperienceIcon
                      className="size-5"
                      pathClassName="stroke-black"
                    />
                    <Text
                      className="font-geologica-regular font-regular text-dark text-sm leading-[1.5] flex-1"
                      numberOfLines={1}
                    >
                      {tutor?.teachingExperience}
                    </Text>
                  </View>

                  {tutor?.examBoardMarker && (
                    <View className="flex-row items-center gap-2">
                      <ExamBoardMarkerIcon
                        className="size-5"
                        pathClassName="stroke-black"
                      />
                      <Text
                        className="font-geologica-regular font-regular text-dark text-sm leading-[1.5] flex-1"
                        numberOfLines={1}
                      >
                        Exam Board marker
                      </Text>
                    </View>
                  )}
                </View>
              </View>

              <View className="h-[1px] w-full bg-teachLoop-creamDark" />

              <Text className="font-geologica-regular font-regular text-base text-black leading-[1.5] flex-1">
                {tutor?.bio}
              </Text>

              {curricula.length > 1 ? (
                <ScrollView
                  horizontal
                  contentContainerClassName="gap-2"
                  showsHorizontalScrollIndicator={false}
                >
                  {curricula.map((curriculum) => (
                    <Tab
                      key={curriculum.id}
                      size="md"
                      intent={
                        curriculum.id === selectedCurriculum?.id
                          ? "active"
                          : "inActive"
                      }
                      onPress={() => setSelectedCurriculumId(curriculum.id)}
                    >
                      <Tab.Text
                        size="md"
                        intent={
                          curriculum.id === selectedCurriculum?.id
                            ? "active"
                            : undefined
                        }
                      >
                        {curriculum.label}
                      </Tab.Text>
                    </Tab>
                  ))}
                </ScrollView>
              ) : null}

              {selectedCurriculum && subjectsForSelectedCurriculum.length > 0 ? (
                <View className="gap-2">
                  <Text className="font-geologica-medium font-medium text-base text-black leading-[1.5]">
                    Subjects ({selectedCurriculum.label})
                  </Text>

                  {subjectsForSelectedCurriculum.map((subjectsWithYearGroup) => (
                    <View
                      className="flex-row gap-2"
                      key={subjectsWithYearGroup.subjectId}
                    >
                      <Text className="font-geologica-medium font-medium text-base text-black leading-[1.5]">
                        {subjectsWithYearGroup.subjectLabel}:
                      </Text>

                      <ScrollView
                        horizontal
                        contentContainerClassName="gap-2"
                        showsHorizontalScrollIndicator={false}
                      >
                        {subjectsWithYearGroup.yearGroups.map((yearGroup) => (
                          <Tag size={"sm"} key={yearGroup.yearGroupId}>
                            <Tag.Text size={"sm"}>
                              {yearGroup.yearGroupLabel}
                            </Tag.Text>
                          </Tag>
                        ))}
                      </ScrollView>
                    </View>
                  ))}
                </View>
              ) : null}

              <View className="gap-2">
                <Text className="font-geologica-medium font-medium text-base text-black leading-[1.5]">
                  Languages spoken
                </Text>

                <Text className="font-geologica-regular font-regular text-base text-dark leading-[1.5]">
                  {[...new Set(tutor?.languageLabels)].join(", ")}
                </Text>
              </View>

              <View className="gap-2">
                <Text className="font-geologica-medium font-medium text-base text-black leading-[1.5]">
                  Latest teaching position
                </Text>

                <Text className="font-geologica-regular font-regular text-base text-dark leading-[1.5]">
                  {tutor?.latestTeachingPosition}
                </Text>
              </View>

              {tutor?.examBoardMarker && (
                <View className="gap-2">
                  <Text className="font-geologica-medium font-medium text-base text-black leading-[1.5]">
                    Exam Board marker details
                  </Text>

                  <Text className="font-geologica-regular font-regular text-base text-dark leading-[1.5]">
                    {tutor?.examBoardMarkerDetails}
                  </Text>
                </View>
              )}

              <View className="gap-2">
                <Text className="font-geologica-medium font-medium text-base text-black leading-[1.5]">
                  Education
                </Text>

                <Text className="font-geologica-regular font-regular text-base text-dark leading-[1.5]">
                  {tutor?.education}
                </Text>
              </View>

              <View ref={reviewsSectionRef} collapsable={false}>
                <TutorProfileReviewsList reviews={approvedReviews} />
              </View>
            </View>

            <View className="gap-4 mb-4">
              {reviewEligibility?.canReview && canBookWithChild && (
                <Button
                  size="md"
                  intent="primary"
                  className="w-full"
                  outline
                  onPress={() => setIsReviewModalVisible(true)}
                >
                  <Button.Text size="md" intent="primary">
                    Write a review
                  </Button.Text>
                </Button>
              )}
              {isTutorStudentSubjectsReady && !canBookWithChild && (
                <Text className="font-geologica-regular text-lg text-gray-500 leading-[1.5] text-center">
                  This tutor is not available for your child&apos;s subjects.
                </Text>
              )}
              {canBookWithChild && canBookIntro && (
                <Button
                  size="md"
                  intent="primary"
                  className="w-full"
                  outline
                  onPress={() =>
                    router.push(
                      `/(app)/booking/lesson-timings?childId=${resolvedChildId}&introCall=true&tutorId=${tutorId}`,
                    )
                  }
                >
                  <Button.Text size="md" intent="primary">
                    Book a free 15 minute intro
                  </Button.Text>
                </Button>
              )}
              {canBookWithChild && (
                <Button
                  size="md"
                  intent="primary"
                  className="w-full"
                  onPress={() =>
                    router.push(
                      `/(app)/booking/lesson-timings?childId=${resolvedChildId}&tutorId=${tutorId}`,
                    )
                  }
                >
                  <Button.Text size="md">Book a lesson</Button.Text>
                </Button>
              )}
            </View>
            </View>
          </View>
        </ResponsiveContent>
      </ScrollView>

      {tutorId && resolvedChildId && tutor && (
        <TutorReviewModal
          isVisible={isReviewModalVisible}
          tutorId={tutorId as Id<"tutorProfiles">}
          studentId={resolvedChildId as Id<"students">}
          tutorName={tutor.name}
          onClose={() => setIsReviewModalVisible(false)}
          onSubmitted={() => {
            void refetchReviewEligibility();
          }}
        />
      )}
    </View>
  );
}

export default TutorProfileScreen;
